import { Location } from 'history';
import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { match, Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { PublishedConnectionTableSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '../../bootstrap/redux/state/AppState';
import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { PublishedConnectionTableSpaceProvider } from './PublishedConnectionTableSpaceProvider';

export interface PublishedConnectionTableRouteParams {
  readonly id: string;
}

export interface PublishedConnectionTableRouteComponentProps extends RouteComponentProps<PublishedConnectionTableRouteParams> {
  readonly match: match<PublishedConnectionTableRouteParams>;
  readonly location: Location;
}

export interface PublishedConnectionTableSpaceRouterOwnProps {
  readonly siteName: string;
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
}

export interface PublishedConnectionTableSpaceRouterStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly catalogingOnSiteEnabled: boolean;
}

export type PublishedConnectionTableSpaceRouterProps =
  PublishedConnectionTableRouteComponentProps &
  PublishedConnectionTableSpaceRouterOwnProps &
  PublishedConnectionTableSpaceRouterStateProps;

export class PublishedConnectionTableSpaceRouterComponent extends React.Component<PublishedConnectionTableSpaceRouterProps, {}> {
  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.virtualConnectionTablePath(':id')} render={(routeProps: PublishedConnectionTableRouteComponentProps) =>
          <Redirect to={this.props.urls.virtualConnectionTableOverviewPath(routeProps.match.params.id)} />
        } />
        <Route exact path={this.props.urls.virtualConnectionTableOverviewPath(':id')} render={this.spaceProviderFor('overview')} />
        <Route exact path={this.props.urls.virtualConnectionTableScheduledTasksPath(':id')} render={this.spaceProviderFor('scheduledTasks')} />
        {this.getLineageRoute()}
        <Route render={() => <RouterError errorType={'default'} urls={this.props.urls} />} />
      </Switch>
    );
  }

  private getLineageRoute = (): JSX.Element => {
    if (FeatureFlags.publishedConnectionsLineageEnabled && this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.virtualConnectionTableLineagePath(':id')} render={this.spaceProviderFor('lineage')} />;
    } else {
      // we want to match on any virtual connection table path starting with /lineage so don't use 'exact' on the route
      return (
        <Route path={this.props.urls.virtualConnectionTableLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.virtualConnectionTableOverviewPath(match.params.id)} />
        )} />);
    }
  };

  private spaceProviderFor = (subplace: PublishedConnectionTableSubspaceName): (routeProps: PublishedConnectionTableRouteComponentProps) => JSX.Element => {
    return (routeProps: PublishedConnectionTableRouteComponentProps) => {
      return (
        <PublishedConnectionTableSpaceProvider
          siteName={this.props.siteName}
          publishedConnectionTableId={routeProps.match.params.id}
          siteUrls={this.props.urls}
          subplaceName={subplace}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          isRequestAccessEnabled={this.props.isRequestAccessEnabled}
        />
      );
    };
  };
}

const mapStateToProps: MapStateToProps<PublishedConnectionTableSpaceRouterStateProps, PublishedConnectionTableSpaceRouterOwnProps, {}> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

export const PublishedConnectionTableSpaceRouter: React.ComponentClass<PublishedConnectionTableSpaceRouterOwnProps>
  = connect(mapStateToProps, null, null, { pure: false })(PublishedConnectionTableSpaceRouterComponent);
